// Migration one-shot des donnees user de localStorage vers IndexedDB (user_data).
// Joue au premier lancement post-S0. Sure au re-run : les rows migrees portent un
// timestamp HISTORIQUE fixe et sont ecrites en LWW-garde (migratePut), donc une
// donnee serveur deja tiree ou une ecriture user posterieure n'est jamais ecrasee.
// Ne migre QUE les cles reellement presentes : un defaut absent ne cree pas de row.
import { migratePut, getSyncFlag, setSyncFlag, getUserData, putUserData } from './userDataStore';
import { DEFAULT_DEMO_PRESET } from '../utils/patternManager';

// Epoch de migration : anterieur a toute vraie donnee (LWW perd contre tout le reste).
export const MIGRATION_TS = '2000-01-01T00:00:00.000Z';
const MIGRATED_FLAG = 'migratedFromLocalStorage';

const num = (s) => parseFloat(s);
const bool = (s) => s === 'true';
const str = (s) => s;
const json = (s) => JSON.parse(s);

// Les 18 settings : cle localStorage -> parser vers valeur typee. La valeur typee
// est stockee directement dans data (les consommateurs lisent du type, plus de string).
const SETTING_KEYS = [
  ['masterVolume', num],
  ['reverbMix', num],
  ['reverbDecay', num],
  ['degreeVolumesDb', json],
  ['globalEqGains', json],
  ['upScale.chordInstrument', str],
  ['pianoVolumeDb', num],
  ['playHighOctave', bool],
  ['upScale.chordExtension', str],
  ['upscale.scaleEntry', str],
  ['upScale.metronome.bpm', num],
  ['upScale.metronome.timeSignature', str],
  ['upScale.metronome.volume', num],
  ['upScale.metronome.visualOnly', bool],
  ['guitarTuning', json],
  ['fretboardLocked', bool],
];

// Collections : cle localStorage (objet { item_key -> objet }) -> scope user_data.
const COLLECTION_KEYS = [
  ['userProgressions', 'progression'],
  ['chord-drone-pattern-presets', 'preset'],
  ['chord-drone-patterns', 'pattern'],
];

// Toutes les cles localStorage migrees, purgees a la FIN de la migration : leur
// verite vit desormais dans user_data (IndexedDB). On vise les cles nommement,
// JAMAIS localStorage.clear(), pour preserver les flags d'onboarding local-only
// (onboardingCompleted, firstTimePopupSeen, hasOpenedBefore) et la session Supabase.
// Inclut bassMode et le legacy staticBass (lus avant la purge par migrateBassMode).
const MIGRATED_KEYS = [
  ...SETTING_KEYS.map(([key]) => key),
  'upScale.bassMode',
  'upScale.staticBass',
  ...COLLECTION_KEYS.map(([key]) => key),
  'artist_purchases',
];

// Migre un setting present. Forme tolerante : un parse qui echoue saute la cle.
async function migrateSetting(db, storage, key, parse) {
  const raw = storage.getItem(key);
  if (raw === null) return 0;
  let value;
  try {
    value = parse(raw);
  } catch (_) {
    return 0;
  }
  if (typeof value === 'number' && Number.isNaN(value)) return 0;
  const written = await migratePut(db, { scope: 'setting', item_key: key, data: value }, MIGRATION_TS);
  return written ? 1 : 0;
}

// bassMode avec repli legacy staticBass : si bassMode absent mais staticBass
// present, derive 'static'/'follow'. staticBass lui-meme n'est PAS migre.
async function migrateBassMode(db, storage) {
  const bassMode = storage.getItem('upScale.bassMode');
  if (bassMode !== null) {
    const w = await migratePut(db, { scope: 'setting', item_key: 'upScale.bassMode', data: bassMode }, MIGRATION_TS);
    return w ? 1 : 0;
  }
  const legacy = storage.getItem('upScale.staticBass');
  if (legacy !== null) {
    const derived = legacy === 'true' ? 'static' : 'follow';
    const w = await migratePut(db, { scope: 'setting', item_key: 'upScale.bassMode', data: derived }, MIGRATION_TS);
    return w ? 1 : 0;
  }
  return 0;
}

// Eclate une collection { item_key -> objet } en une row user_data par entree.
// L'objet est stocke TEL QUEL (preserve timings/loopDuration/recordedBars, etc.).
async function migrateCollection(db, storage, key, scope) {
  const raw = storage.getItem(key);
  if (raw === null) return 0;
  let obj;
  try {
    obj = JSON.parse(raw);
  } catch (_) {
    return 0;
  }
  let count = 0;
  for (const [itemKey, value] of Object.entries(obj || {})) {
    const w = await migratePut(db, { scope, item_key: itemKey, data: value }, MIGRATION_TS);
    if (w) count += 1;
  }
  return count;
}

// Achats : array d'ids artistes -> une row purchase par id.
async function migratePurchases(db, storage) {
  const raw = storage.getItem('artist_purchases');
  if (raw === null) return 0;
  let ids;
  try {
    ids = JSON.parse(raw);
  } catch (_) {
    return 0;
  }
  let count = 0;
  for (const id of Array.isArray(ids) ? ids : []) {
    const w = await migratePut(db, { scope: 'purchase', item_key: id, data: { purchased: true } }, MIGRATION_TS);
    if (w) count += 1;
  }
  return count;
}

// Seed du demo-preset, independant de la migration : une install neuve n'a pas
// de localStorage donc ne migre rien ; sans ce seed, le preset par defaut
// disparaitrait pour les nouveaux users. Idempotent (skip si deja present).
export async function ensureDemoPreset(db) {
  const existing = await getUserData(db, 'pattern', DEFAULT_DEMO_PRESET.id);
  if (existing) return false;
  await putUserData(
    db,
    { scope: 'pattern', item_key: DEFAULT_DEMO_PRESET.id, data: DEFAULT_DEMO_PRESET },
    MIGRATION_TS
  );
  return true;
}

// Migration one-shot. Idempotente via le flag, mais sure meme si rejouee.
export async function migrateLocalStorage(db, storage = (typeof localStorage !== 'undefined' ? localStorage : null)) {
  if (!storage) return { skipped: 'no-storage' };
  if (await getSyncFlag(db, MIGRATED_FLAG)) return { skipped: 'already-migrated' };

  let migrated = 0;
  for (const [key, parse] of SETTING_KEYS) migrated += await migrateSetting(db, storage, key, parse);
  migrated += await migrateBassMode(db, storage);
  for (const [key, scope] of COLLECTION_KEYS) migrated += await migrateCollection(db, storage, key, scope);
  migrated += await migratePurchases(db, storage);

  await setSyncFlag(db, MIGRATED_FLAG, MIGRATION_TS);

  // Purge les cles legacy une fois leur valeur en lieu sur dans user_data. Apres
  // toutes les lectures (le repli staticBass de migrateBassMode a deja joue).
  for (const key of MIGRATED_KEYS) {
    try { storage.removeItem(key); } catch (_) { /* storage en lecture seule : on laisse */ }
  }

  return { migrated };
}
